/**
 * @file Локальный таймер хода героя для онлайн-белота.
 *
 * Сервер транслирует `turnDeadline`, поэтому таймер считается от
 * authoritative дедлайна (fallback: локальный лимит `turnSeconds`).
 * Таймер служит двум целям:
 *
 *  - Отображение оставшихся секунд (в шапке и/или у места героя).
 *  - Проигрывание звука `your-turn` в момент, когда наступила очередь
 *    героя — точно так же, как делает локальный
 *    {@link useBelotHeroTurnTimer}.
 *
 * Никаких авто-PASS/авто-PLAY: на онлайне за тайм-аутом следит сервер.
 * В `auction`/`play` без числового `turnDeadlineAtMs` локальный отсчёт не подменяем — иначе «0 сек» не совпадает с сервером.
 *
 * @module components/online/hooks/useBelotOnlineHeroTurnTimer
 */

import { useEffect, useRef, useState } from 'react';

export const ONLINE_BELOT_HERO_TURN_SECONDS = 30;

/**
 * @param {object} args
 * @param {object|null} args.belotArmenian — публичный снапшот.
 * @param {number|null} args.mySeat — место героя (0..3) или `null`.
 * @param {(soundId: string, volume?: number) => void} args.play
 * @param {number} [args.turnSeconds]
 * @param {boolean} [args.suppressed] — legacy: mute только звук `your-turn`.
 * @param {boolean} [args.audioSuppressed] — не проигрывать `your-turn` (autoplay).
 * @param {boolean} [args.blockHeroTurnTimer] — не показывать/не считать таймер (раздача, reveal-оверлей).
 * @param {boolean} [args.openDuellPlaySeats] — Open 1×1: в розыгрыше ход на местах 0..3, герой играет с руки (seat) и со стола (seat+2).
 * @param {number} [args.turnResyncKey] — монотонный ключ (напр. `belotSync.seq`), чтобы сбросить таймер/звук при повторном ходе героя в той же взятке.
 * @returns {{ heroSecLeft: number|null, heroTimedTurn: boolean }}
 */
export function useBelotOnlineHeroTurnTimer({
  belotArmenian,
  mySeat,
  play,
  turnSeconds = ONLINE_BELOT_HERO_TURN_SECONDS,
  suppressed = false,
  audioSuppressed: audioSuppressedProp,
  blockHeroTurnTimer = false,
  turnDeadlineAtMs = null,
  openDuellPlaySeats = false,
  turnResyncKey = 0,
}) {
  const phase = belotArmenian?.phase || '';
  const turnSeat =
    typeof belotArmenian?.currentTurnSeat === 'number' ? belotArmenian.currentTurnSeat : null;
  const trickLen = Array.isArray(belotArmenian?.currentTrick) ? belotArmenian.currentTrick.length : 0;
  const heroPlaySeatMatch =
    turnSeat != null &&
    mySeat != null &&
    (openDuellPlaySeats ? turnSeat === mySeat || turnSeat === mySeat + 2 : turnSeat === mySeat);
  const audioSuppressed = audioSuppressedProp !== undefined ? audioSuppressedProp : suppressed;
  const heroTimedTurn =
    !blockHeroTurnTimer &&
    mySeat != null &&
    ((phase === 'auction' && turnSeat === mySeat) || (phase === 'play' && heroPlaySeatMatch));

  const [heroSecLeft, setHeroSecLeft] = useState(null);
  const prevHeroTimedTurnRef = useRef(false);

  useEffect(() => {
    if (!heroTimedTurn) {
      setHeroSecLeft(null);
      return undefined;
    }
    const hasDeadline = Number.isFinite(Number(turnDeadlineAtMs));
    if (!hasDeadline && (phase === 'auction' || phase === 'play')) {
      setHeroSecLeft(null);
      return undefined;
    }
    const deadline = hasDeadline ? Number(turnDeadlineAtMs) : Date.now() + turnSeconds * 1000;
    const compute = () => Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    setHeroSecLeft(compute());
    const id = window.setInterval(() => {
      const v = compute();
      setHeroSecLeft(v);
      if (hasDeadline && Date.now() >= deadline && v <= 0) {
        window.clearInterval(id);
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [
    heroTimedTurn,
    turnSeconds,
    phase,
    turnSeat,
    turnDeadlineAtMs,
    openDuellPlaySeats,
    mySeat,
    trickLen,
    turnResyncKey,
    blockHeroTurnTimer,
  ]);

  useEffect(() => {
    if (heroTimedTurn && !prevHeroTimedTurnRef.current && !audioSuppressed) {
      play('your-turn', 0.82);
    }
    prevHeroTimedTurnRef.current = heroTimedTurn;
  }, [heroTimedTurn, play, audioSuppressed]);

  return { heroSecLeft, heroTimedTurn };
}
